import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { link_click } from '../redux/actions';
import NotificationService from '../Services/NotificationService';
import FileForDownloadService from '../Services/FileForDownloadService';
import ErrorMessage from './ErrorMessage';
import { showNotificationPopup } from './NotificationPopupHost';

// Static string-literal import() => own chunk, no import-context sweep.
const Messages = lazy(() => import('../Contents/Messages/Messages'));
const Downloads = lazy(() => import('../Contents/Downloads/Downloads'));

// Polls `fetchCount` (a function returning Promise<number>) on mount and every
// 60s; in dev it polls once (matches the legacy ErrorMessage.NotDev() guard).
// Calls `onIncrease(count)` whenever the count grows past the previous poll.
// 600s backoff after an error. Mirrors the legacy populateData() cadence.
function usePoll(fetchCount, onIncrease) {
  const prev = useRef(0);
  const onIncreaseRef = useRef(onIncrease);
  onIncreaseRef.current = onIncrease;

  useEffect(() => {
    let timer = null;
    let cancelled = false;
    const schedule = (ms) => { timer = window.setTimeout(poll, ms); };
    const poll = () => {
      Promise.resolve(fetchCount())
        .then((count) => {
          if (cancelled) return;
          if (count > prev.current) onIncreaseRef.current(count);
          prev.current = count;
          if (ErrorMessage.NotDev()) schedule(60000);
        })
        .catch(() => { if (!cancelled) schedule(600000); });
    };
    poll();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, []);
}

// Polls unread-message count; on an increase shows the popup with a preview of
// the latest unread message, and restores the background-process-result popups.
export function MessagePopupTrigger() {
  const dispatch = useDispatch();

  usePoll(
    () => NotificationService.GetNumberOfUnreadMessages().then((r) => (r ? r.result : 0)),
    (count) => {
      const title = `${count} unread message${count === 1 ? '' : 's'}`;
      const onView = () =>
        dispatch(link_click(
          <Suspense fallback={<div />}><Messages /></Suspense>,
          'Messages', 'fa-regular fa-envelope', null, 0, 'messages'
        ));

      // Latest unread message for the preview line (best-effort).
      NotificationService.GetMessageList()
        .then((list) => {
          const unread = (list || [])
            .filter((m) => !m.isRead)
            .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
          const latest = unread[0];
          showNotificationPopup({
            id: 'messages', count, title,
            preview: latest ? { from: latest.fromUserName || 'Unknown', subject: latest.subject || '' } : undefined,
            accent: 'red', glyph: 'fa-regular fa-envelope', ctaLabel: 'View inbox', onView,
          });
        })
        .catch(() => {
          showNotificationPopup({
            id: 'messages', count, title, accent: 'red', glyph: 'fa-regular fa-envelope', ctaLabel: 'View inbox', onView,
          });
        });

      // Restored regression (CD-5276): background-process-result popups, which
      // GetPopupMessages fetches (server clears the flag so each pops once) and
      // ErrorMessage.SetInfo shows via the already-DS-styled FigmaPopup.
      NotificationService.GetPopupMessages()
        .then((messages) => {
          if (!messages || messages.length === 0) return;
          const body = (
            <div>
              {messages.map((m, index) => (
                <div key={`popupmsg${index}`} className={index > 0 ? 'mt-3' : ''}>
                  {m.subject ? <div className='fw-bold'>{m.subject}</div> : null}
                  <div>{m.message}</div>
                </div>
              ))}
            </div>
          );
          ErrorMessage.SetInfo(body);
        })
        .catch((error) => ErrorMessage.SetErrorMessage('NUM10', error));
    }
  );

  return null;
}

// Polls undownloaded-file count; on an increase shows a count-only popup.
export function DownloadPopupTrigger() {
  const dispatch = useDispatch();

  usePoll(
    () => FileForDownloadService.GetNumberOfUndownloadedFiles().then((r) => (r ? r.numberOfFiles : 0)),
    (count) => {
      const onView = () =>
        dispatch(link_click(
          <Suspense fallback={<div />}><Downloads /></Suspense>,
          'Downloads', 'fa-solid fa-download', null, 0, 'downloads'
        ));
      showNotificationPopup({
        id: 'downloads', count,
        title: `${count} file${count === 1 ? '' : 's'} to download`,
        accent: 'info', glyph: 'fa-solid fa-download', ctaLabel: 'View downloads', onView,
      });
    }
  );

  return null;
}
